import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';

import { DriverStatusHandler } from '../lib/sync/driver-status-handler';
import { FollowerSync } from '../lib/sync/follower-sync';
import { ReconnectionHandler } from '../lib/sync/reconnection-handler';
import { processTransportCommand } from '../lib/sync/transport-sync';
import type {
  ClockInfo,
  ConnectionState,
  DriverStatus,
  HeartbeatBroadcast,
  TransportBroadcast,
  TransportCommandType,
  TransportLogEntry,
} from '../lib/sync/types';
import { VisibilityManager } from '../lib/sync/visibility-manager';
import { WakeLock } from '../lib/sync/wake-lock';

export interface UseFollowerSyncOptions {
  socket: Socket | null;
  sessionId: string | null;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (timeMs: number, sectionIndex: number) => void;
  onAdjustRate: (rate: number) => void;
}

export interface UseFollowerSyncReturn {
  driftMs: number;
  connectionState: ConnectionState;
  driverStatus: DriverStatus;
  clockOffset: number;
  clockRtt: number;
  heartbeatCount: number;
  lastHeartbeatMs: number | null;
  playbackRate: number;
  transportLog: TransportLogEntry[];
}

interface ClockState {
  offset: number;
  rtt: number;
}

function performClockSync(socket: Socket): Promise<ClockState> {
  return new Promise((resolve) => {
    const samples: { offset: number; rtt: number }[] = [];
    let count = 0;

    function sendPing() {
      const t1 = Date.now();
      socket.emit('clock:ping', { t1 });

      const handler = (data: { t1: number; t2: number }) => {
        const t3 = Date.now();
        const rtt = t3 - data.t1;
        const offset = data.t2 - data.t1 - rtt / 2;
        samples.push({ offset, rtt });
        count++;

        socket.off('clock:pong', handler);

        if (count < 3) {
          sendPing();
        } else {
          // Use sample with lowest RTT
          samples.sort((a, b) => a.rtt - b.rtt);
          resolve(samples[0]);
        }
      };

      socket.on('clock:pong', handler);
    }

    sendPing();
  });
}

export function useFollowerSync({
  socket,
  sessionId,
  onPlay,
  onPause,
  onSeek,
  onAdjustRate,
}: UseFollowerSyncOptions): UseFollowerSyncReturn {
  const [driftMs, setDriftMs] = useState(0);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connected');
  const [driverStatus, setDriverStatus] = useState<DriverStatus>('connected');
  const [clockOffset, setClockOffset] = useState(0);
  const [clockRtt, setClockRtt] = useState(0);
  const [heartbeatCount, setHeartbeatCount] = useState(0);
  const [lastHeartbeatMs, setLastHeartbeatMs] = useState<number | null>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const transportLogRef = useRef<TransportLogEntry[]>([]);
  const sessionStartRef = useRef<number | null>(null);

  const clockRef = useRef<ClockState>({ offset: 0, rtt: 0 });
  const followerSyncRef = useRef<FollowerSync | null>(null);
  const reconnectionRef = useRef(new ReconnectionHandler());
  const driverStatusRef = useRef(new DriverStatusHandler());
  const visibilityRef = useRef<VisibilityManager | null>(null);
  const wakeLockRef = useRef<WakeLock | null>(null);

  const pushLogEntry = useCallback((command: string, extra?: Partial<TransportLogEntry>) => {
    const now = Date.now();
    if (sessionStartRef.current === null) {
      sessionStartRef.current = now;
    }
    transportLogRef.current.push({
      ts: now,
      relativeMs: now - sessionStartRef.current,
      command,
      ...extra,
    });
  }, []);

  const handleTransportCommand = useCallback(
    (command: TransportCommandType, broadcast: TransportBroadcast) => {
      const instruction = processTransportCommand(command, broadcast);
      pushLogEntry(command, {
        sectionIndex: broadcast.sectionIndex,
        timeMs: broadcast.timeMs,
      });
      switch (instruction.type) {
        case 'play':
          onSeek(instruction.timeMs, instruction.sectionIndex);
          onPlay();
          break;
        case 'pause':
          onSeek(instruction.timeMs, instruction.sectionIndex);
          onPause();
          break;
        case 'seek':
          onSeek(instruction.timeMs, instruction.sectionIndex);
          break;
        case 'reset':
          onSeek(0, 0);
          onPause();
          break;
      }
    },
    [onPlay, onPause, onSeek, pushLogEntry],
  );

  useEffect(() => {
    if (!socket || !sessionId) return;

    const visibilityManager = new VisibilityManager();
    visibilityRef.current = visibilityManager;

    const wakeLock = new WakeLock();
    wakeLockRef.current = wakeLock;
    wakeLock.acquire();

    const clockInfo: ClockInfo = {
      getClockOffset: () => clockRef.current.offset,
      getRTT: () => clockRef.current.rtt,
    };

    // Do clock sync
    performClockSync(socket).then((clock) => {
      clockRef.current = clock;
      setClockOffset(clock.offset);
      setClockRtt(clock.rtt);
    });

    const followerSync = new FollowerSync({
      clock: clockInfo,
      getLocalTimeMs: () => 0, // Will be set by heartbeat context
      visibilityManager,
    });
    followerSyncRef.current = followerSync;

    reconnectionRef.current.onSessionJoined(sessionId);

    // Heartbeat handler
    socket.on('heartbeat', (data: HeartbeatBroadcast) => {
      driverStatusRef.current.onHeartbeatReceived();
      setHeartbeatCount((c) => c + 1);
      setLastHeartbeatMs(Date.now());
      const result = followerSync.processHeartbeat(data);
      if (result) {
        setDriftMs(result.driftMs);
        pushLogEntry('heartbeat', {
          driftMs: result.driftMs,
          sectionIndex: data.sectionIndex,
          timeMs: data.timeMs,
        });
        if (result.action.type === 'adjust_rate') {
          setPlaybackRate(result.action.playbackRate);
          onAdjustRate(result.action.playbackRate);
        } else if (result.action.type === 'seek') {
          onSeek(result.action.targetTimeMs, data.sectionIndex);
          followerSync.onSeekApplied();
        }
      }
    });

    // Transport commands
    const transportEvents: TransportCommandType[] = ['play', 'pause', 'seek', 'reset'];
    for (const cmd of transportEvents) {
      socket.on(`transport:${cmd}`, (broadcast: TransportBroadcast) => {
        handleTransportCommand(cmd, broadcast);
      });
    }

    // Driver status
    socket.on('session:driver_disconnected', () => {
      driverStatusRef.current.onDriverDisconnected();
      setDriverStatus('disconnected');
    });

    socket.on('session:driver_reconnected', () => {
      driverStatusRef.current.onDriverReconnected();
      setDriverStatus('reconnected');
    });

    // Reconnection
    socket.on('disconnect', () => {
      reconnectionRef.current.onDisconnect();
      setConnectionState('disconnected');
    });

    socket.on('reconnect', () => {
      const instruction = reconnectionRef.current.onReconnected();
      setConnectionState('reconnecting');
      if (instruction?.type === 'rejoin') {
        socket.emit('session:rejoin', { sessionId: instruction.sessionId });
      }
    });

    socket.on('session:state', (payload) => {
      const instruction = reconnectionRef.current.processSessionState(payload);
      setConnectionState('connected');
      switch (instruction.type) {
        case 'seek_and_play':
          onSeek(instruction.timeMs, instruction.sectionIndex);
          onPlay();
          break;
        case 'seek_and_pause':
          onSeek(instruction.timeMs, instruction.sectionIndex);
          onPause();
          break;
        case 'reset':
          onSeek(0, 0);
          onPause();
          break;
      }
    });

    return () => {
      visibilityManager.destroy();
      wakeLock.release();
      followerSyncRef.current = null;

      socket.off('heartbeat');
      for (const cmd of transportEvents) {
        socket.off(`transport:${cmd}`);
      }
      socket.off('session:driver_disconnected');
      socket.off('session:driver_reconnected');
      socket.off('disconnect');
      socket.off('reconnect');
      socket.off('session:state');
    };
  }, [
    socket,
    sessionId,
    onPlay,
    onPause,
    onSeek,
    onAdjustRate,
    handleTransportCommand,
    pushLogEntry,
  ]);

  return {
    driftMs,
    connectionState,
    driverStatus,
    clockOffset,
    clockRtt,
    heartbeatCount,
    lastHeartbeatMs,
    playbackRate,
    transportLog: transportLogRef.current,
  };
}
